import { useEffect, useLayoutEffect, useRef, useState, useMemo } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { trackSearch } from "../lib/metaPixel";
import { pushSearch, pushViewItemList } from "../lib/gtmDataLayer";
import { SITE_ORIGIN, PATH_TO_CATEGORY, CATEGORY_TO_PATH, productUrl, categoryPathFor } from "../lib/seo";
import { useCategoriesWithMeta } from "../hooks/useCategories";

const CATS_FALLBACK = ["All", "Pickles", "Culinary Pastes", "Spices", "Masalas", "Traditional Powders", "Flours", "Wood Pressed Oils", "Combos"];

// iter63 — per-category SEO copy used when the route is /pickles, /spices, etc.
const CATEGORY_SEO = {
  "Pickles": {
    title: "Andhra Pickles | Avakaya, Gongura, Lemon & More – Sri Durga Foods",
    description: "Authentic Andhra pickles handmade in Nellore — Avakaya, Gongura, Nimmakaya, Pandu Mirchi and 16+ traditional pachadis. No preservatives. Order online.",
    intro: "Bold, sun-cured Andhra pickles — small-batch and handmade in our Nellore kitchen.",
  },
  "Culinary Pastes": {
    title: "Culinary Pastes | Ginger Garlic, Pulihora – Sri Durga Foods",
    description: "Fresh culinary pastes — Ginger Garlic, Pulihora and Mango Pulihora. No preservatives, ready to use. Direct from our Andhra kitchen.",
    intro: "Freshly ground pastes that save time without compromising authentic flavour.",
  },
  "Wood Pressed Oils": {
    title: "Wood Pressed Oils | Chekku Groundnut & Coconut – Sri Durga Foods",
    description: "Pure wood-pressed (chekku) groundnut and coconut oils — low-temperature extraction, unrefined, no chemicals. Healthier cooking oils delivered pan-India.",
    intro: "Wood-pressed oils extracted slowly to preserve aroma and nutrients.",
  },
  "Traditional Powders": {
    title: "Andhra Podis | Idli Podi, Kobbari Karam, Nalla Karam – Sri Durga Foods",
    description: "Traditional Andhra podis — Idli, Kobbari, Karivepaku, Nalla Karam, Pappula and Verusenaga. Hand-roasted and stone-ground for authentic flavour.",
    intro: "Hand-ground podis that turn a bowl of rice and ghee into a feast.",
  },
  "Masalas": {
    title: "Andhra Masala Powders | Sambar, Rasam, Chicken, Mutton – Sri Durga Foods",
    description: "Andhra-style sambar, rasam, chicken, mutton, egg and garam masala powders. Small-batch roasted and stone-ground. Buy authentic masalas online.",
    intro: "Stone-ground Andhra masalas — the real flavour that elevates every curry.",
  },
  "Spices": {
    title: "Pure Spices | Guntur Chilli, Turmeric, Coriander, Hing – Sri Durga Foods",
    description: "Pure single-origin spices — Guntur red chilli powder, turmeric, coriander and asafoetida. No fillers, no synthetic colour. Direct from Andhra Pradesh.",
    intro: "Single-origin spices — pure, potent and unblended.",
  },
  "Flours": {
    title: "Stone-Ground Flours | Ragi Flour & More – Sri Durga Foods",
    description: "Stone-ground ragi (finger millet) and traditional flours — nutrient-dense, slow-milled. Perfect for ragi mudde, dosa and healthy bakes.",
    intro: "Stone-ground flours retaining bran, germ and natural nutrients.",
  },
  "Combos": {
    title: "Andhra Combo Packs | Value Bundles – Sri Durga Foods",
    description: "Sri Durga Foods combo packs — curated bundles of our best pickles, pastes, spices and masalas at combo pricing. Pan-India delivery.",
    intro: "Curated combo bundles — save more on our best-loved Andhra classics.",
  },
};

// In-memory product cache survives navigation within the SPA so that
// returning from /product/:id back to /shop renders instantly with no
// loading state, no grid rebuild and the exact previous scroll position.
// The cache is intentionally module-scoped — it's read-only catalog data
// and never user-specific.
const productCache = new Map();   // key -> products[]
const scrollCache = new Map();    // key -> last scrollY
const cacheKeyFor = (category, search) => `${category}::${search}`;

export default function Shop({ categorySlug } = {}) {
  // iter76 — live category list from Admin → Categories, with "All" prepended.
  const { categories: liveCategories, loaded: liveCategoriesLoaded } = useCategoriesWithMeta();
  const CATS = useMemo(
    () => ["All", ...liveCategories.map((c) => c.name)],
    [liveCategories]
  );
  const [params, setParams] = useSearchParams();
  const routeParams = useParams();
  const navigate = useNavigate();
  // iter79 — the dynamic /:categorySlug catch-all route passes the slug
  // via useParams; the legacy hardcoded /pickles etc. routes pass it as
  // a component prop.  Either source is honoured.
  const effectiveCategorySlug = categorySlug || routeParams.categorySlug || null;
  // iter63 — when route is /pickles, /spices etc. the categorySlug prop
  // wins over any ?category= query param.  This keeps the SEO URL
  // canonical and avoids duplicate-content via /shop?category=Pickles
  // AND /pickles for the same listing.
  // iter79 — resolve dynamic (admin-created) category slugs against the
  // live categories list so /whole-spices maps back to "Whole Spices".
  const slugCategory = effectiveCategorySlug
    ? (PATH_TO_CATEGORY[effectiveCategorySlug]
        || liveCategories.find((c) => categoryPathFor(c.name) === effectiveCategorySlug)?.name
        || null)
    : null;
  const category = slugCategory || params.get("category") || "All";
  const search = params.get("search") || "";
  const cacheKey = cacheKeyFor(category, search);
  const cached = productCache.get(cacheKey);

  // iter79 — if the URL carries a category slug that doesn't resolve to
  // any real category (curated OR admin-created), redirect to /shop.
  // GATE on `liveCategoriesLoaded` (not `.length`) because the hook
  // ALWAYS pre-seeds a fallback list — using length would fire the
  // redirect against stale data before the API responds.
  useEffect(() => {
    if (!effectiveCategorySlug) return;
    if (!liveCategoriesLoaded) return;
    if (!slugCategory) navigate("/shop", { replace: true });
  }, [effectiveCategorySlug, slugCategory, liveCategoriesLoaded, navigate]);

  const [products, setProducts] = useState(cached || []);
  // Only show "Loading…" on the FIRST visit for this filter combo.
  const [loading, setLoading] = useState(!cached);

  // Auto-hide category filter bar on scroll down; reveal on scroll up.
  // Pure transform-based slide. No fade, no opacity animation.
  // Two parallel triggers so BOTH slow trickle-scrolls and fast jumps work:
  //  • FAST path  → any single rAF tick with |dy| ≥ FAST_DY flips immediately.
  //  • SLOW path  → accumulated delta in one direction crossing SLOW_ACCUM
  //                 flips (so slow finger-drag scrolls also trigger).
  // Direction-change resets the slow accumulator so opposite scroll is
  // responsive without waiting for the previous direction to bleed off.
  const [barHidden, setBarHidden] = useState(false);
  const lastYRef = useRef(0);
  const accumRef = useRef(0);
  useEffect(() => {
    const FAST_DY = 24;     // single-frame delta that flips immediately
    const SLOW_ACCUM = 24;  // accumulated delta in one direction
    const SHOW_FROM_TOP = 80;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - lastYRef.current;
        if ((dy > 0 && accumRef.current < 0) || (dy < 0 && accumRef.current > 0)) {
          accumRef.current = 0;
        }
        accumRef.current += dy;

        if (y < SHOW_FROM_TOP) {
          setBarHidden(false);
          accumRef.current = 0;
        } else if (dy >= FAST_DY || accumRef.current >= SLOW_ACCUM) {
          setBarHidden(true);
          accumRef.current = 0;
        } else if (dy <= -FAST_DY || accumRef.current <= -SLOW_ACCUM) {
          setBarHidden(false);
          accumRef.current = 0;
        }
        lastYRef.current = y;
        ticking = false;
      });
    };
    lastYRef.current = window.scrollY;
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Persist scrollY just before this page unmounts (i.e. customer opens a
  // product) so we can restore it pixel-perfect on Back.
  useEffect(() => {
    return () => {
      scrollCache.set(cacheKey, window.scrollY);
    };
  }, [cacheKey]);

  // Synchronously restore the saved scrollY BEFORE the next paint when the
  // grid is already cache-hydrated. We re-assert the position for ~450ms to
  // outlast ScrollManager's own POP-restoration settle window (≤250ms) and
  // any late layout shifts caused by sticky-header re-paints.
  useLayoutEffect(() => {
    if (!cached) return;
    const y = scrollCache.get(cacheKey);
    if (y == null) return;
    window.scrollTo(0, y);
    const start = performance.now();
    const REASSERT_MS = 450;
    let rafId = 0;
    let aborted = false;
    const onUserInteract = () => { aborted = true; };
    const events = ["wheel", "touchstart", "keydown", "mousedown", "pointerdown"];
    events.forEach((ev) => window.addEventListener(ev, onUserInteract, { passive: true, once: true }));
    const reassert = () => {
      if (aborted) return;
      if (Math.abs(window.scrollY - y) > 2) window.scrollTo(0, y);
      if (performance.now() - start < REASSERT_MS) {
        rafId = requestAnimationFrame(reassert);
      }
    };
    rafId = requestAnimationFrame(reassert);
    return () => {
      cancelAnimationFrame(rafId);
      events.forEach((ev) => window.removeEventListener(ev, onUserInteract));
    };
  }, [cacheKey, cached]);

  useEffect(() => {
    const fromCache = productCache.get(cacheKey);
    if (fromCache) {
      setProducts(fromCache);
      setLoading(false);
    } else {
      setLoading(true);
    }
    // Always background-refresh so the user gets the latest catalog data
    // without a visible loading flicker on subsequent visits.
    const q = {};
    if (category !== "All") q.category = category;
    if (search) q.search = search;
    api.get("/products", { params: q })
      .then((r) => {
        productCache.set(cacheKey, r.data);
        setProducts(r.data);
        // iter52 — fire Search ONLY when a query string is present; this
        // is the e-commerce Search standard event for Meta.
        if (search && search.trim()) {
          trackSearch(search, Array.isArray(r.data) ? r.data.length : undefined);
          pushSearch(search, Array.isArray(r.data) ? r.data.length : undefined);
        }
        // iter57 — GA4 view_item_list for the product grid.
        pushViewItemList(r.data, {
          listId: (category || "all").toLowerCase().replace(/\s+/g, "_"),
          listName: category || "All Products",
        });
      })
      .finally(() => setLoading(false));
  }, [category, search, cacheKey]);

  const setCat = (c) => {
    // iter91 — always navigate to the canonical category route.
    //
    // Previously we consulted the hardcoded CATEGORY_TO_PATH map and
    // fell back to `setParams({ category: c })` for admin-created
    // categories like "Whole Spices".  That fallback silently failed
    // whenever the user was already on a categorySlug route: e.g.
    // clicking Whole Spices from /pickles produced
    // `/pickles?category=Whole+Spices` where the pathname-derived
    // slugCategory ("Pickles") won over the query param → the chip
    // briefly flashed the tap-active colour but the products never
    // switched.  Bug shows up every time you go categorySlug → dynamic
    // category → another categorySlug → dynamic category.
    //
    // categoryPathFor() honours the curated map for well-known
    // categories AND slugifies admin-created ones (Whole Spices →
    // whole-spices), matched by the catch-all /:categorySlug route.
    if (c === "All") {
      navigate("/shop");
      return;
    }
    navigate(`/${categoryPathFor(c)}`);
  };

  const heading = search
    ? `Results for "${search}"`
    : category === "All"
      ? "The full pantry."
      : `Our ${category}.`;

  // iter81 — resolve SEO metadata dynamically.
  //  1. If the current category has admin-set `seo_title` / `seo_description`
  //     in the DB (via useCategoriesWithMeta), those override everything.
  //  2. Curated CATEGORY_SEO entries handle well-known slugs.
  //  3. Admin-created categories fall back to a clean auto-generated
  //     title/description — no more "Products - Sri Durga Foods".
  const currentCategoryDoc = category !== "All"
    ? liveCategories.find((c) => c.name === category)
    : null;
  const adminSeoTitle = currentCategoryDoc?.seo_title || "";
  const adminSeoDescription = currentCategoryDoc?.seo_description || "";

  const seoTitle = adminSeoTitle
    || (slugCategory && CATEGORY_SEO[slugCategory] ? CATEGORY_SEO[slugCategory].title : "")
    || (search
      ? `Search results for "${search}" | Sri Durga Foods`
      : category === "All"
        ? "Shop the Collection | Andhra Pickles, Spices, Oils | Sri Durga Foods"
        : `${category} | Sri Durga Foods`);
  const seoDescription = adminSeoDescription
    || (slugCategory && CATEGORY_SEO[slugCategory] ? CATEGORY_SEO[slugCategory].description : "")
    || (category !== "All"
      ? `Shop authentic ${category} from Sri Durga Foods — handcrafted in Nellore, pan-India delivery. Pure ingredients, traditional recipes, uncompromised quality.`
      : "Browse Sri Durga Foods — authentic Andhra pickles, pastes, spices, masalas, podis, flours and wood-pressed oils. Pan-India delivery.");
  const canonicalPath = slugCategory
    ? `/${categoryPathFor(slugCategory)}`
    : category !== "All"
      ? `/${categoryPathFor(category)}`
      : "/shop";
  const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`;

  // CollectionPage JSON-LD with each product as a list item — helps
  // Google understand category landing pages as Rich Results.
  const collectionJsonLd = {
    "@context": "https://schema.org/",
    "@type": "CollectionPage",
    "name": seoTitle,
    "description": seoDescription,
    "url": canonicalUrl,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": products.length,
      "itemListElement": products.slice(0, 50).map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": `${SITE_ORIGIN}${productUrl(p)}`,
        "name": p.name,
      })),
    },
  };

  // Breadcrumb schema helps Google render "Home › Pickles" in the SERP.
  const breadcrumbJsonLd = category !== "All" ? {
    "@context": "https://schema.org/",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_ORIGIN}/` },
      { "@type": "ListItem", "position": 2, "name": category, "item": canonicalUrl },
    ],
  } : null;

  return (
    <div className="container-x py-12 lg:py-20" data-testid="shop-page">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalPath}
        image={currentCategoryDoc?.og_image || undefined}
        // iter81 — in-site search results are marked noindex per Google's
        // guidance; category pages remain fully indexable.
        noindex={!!search}
      >
        <script type="application/ld+json">{JSON.stringify(collectionJsonLd)}</script>
        {breadcrumbJsonLd && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        )}
      </SEOHead>
      <div className="max-w-3xl mb-10">
        <span className="overline mb-3 block">
          {search ? "Search" : "Collections"}
        </span>
        <h1 className="font-serif text-5xl sm:text-6xl text-brand-dark leading-tight" data-testid="shop-heading">
          {heading}
        </h1>
        <p className="text-sm text-brand-muted mt-4 max-w-xl">
          {products.length} hand-made product{products.length === 1 ? "" : "s"}, freshly prepared and shipped pan-India.
        </p>
      </div>

      <div
        className={`mb-10 border-b border-brand-line pb-6 sticky top-[68px] lg:top-[78px] bg-brand-cream/90 backdrop-blur z-30 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 pt-4 will-change-transform transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          barHidden ? "-translate-y-[140%]" : "translate-y-0"
        }`}
        aria-hidden={barHidden}
        data-testid="category-filters"
        data-hidden={barHidden ? "true" : "false"}
      >
        {/*
          iter89/iter90 — Category chip layout.
          Desktop (sm+): centred cluster in a `max-w-md` container → 4-3-3 rows
            (matches the user's spec exactly with the current 9 live categories).
          Mobile: left-aligned, tighter chip padding so the same 4-3-3 rows fit
            within the 375-px viewport without wrapping into a 4th row.
        */}
        <div className="flex flex-wrap justify-start sm:justify-center gap-x-1.5 gap-y-2 sm:gap-x-2 sm:max-w-md sm:mx-auto">
          {CATS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`text-xs sm:text-sm px-3 sm:px-4 py-1.5 rounded-full font-medium tracking-wide transition-all whitespace-nowrap ${
                category === c && !search
                  ? "bg-brand-dark text-white"
                  : "bg-transparent text-brand-dark hover:bg-brand-chili/10 active:bg-brand-chili active:text-white"
              }`}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-32 text-brand-muted">Loading…</div>
      ) : products.length === 0 ? (
        <div className="text-center py-32 text-brand-muted" data-testid="no-products">
          No products found{search ? ` for "${search}"` : ""}.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-8">
          {products.map((p, i) => <ProductCard key={p.product_id} p={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
